/**
 * Games Store Flow:
 *
 * 1. Initial Load (`startStore`):
 *    - When the store initializes, it calls `fetchAndUpdateRooms` in FULL mode:
 *      • Reads `roomIdCounter` and `activeRoomCounter` from the blockchain
 *      • If these counters changed, it fetches room data in batches (`MAX_ROOMS_PER_BATCH`)
 *      • Rooms are processed from newest to oldest, up to `MAX_ACTIVE_ROOMS_TO_FETCH`
 *      • Each room is categorized into one of the main room lists (see below)
 *
 * 2. Room Categories:
 *    - Rooms are organized into 4 categories:
 *      • `activeRooms`: rooms with two players and still in progress
 *      • `activeSingle`: rooms with only one player (waiting for opponent)
 *      • `playedRooms`: finished games (both players and closed)
 *      • `playedRoomsFirstBatch`: quick-access subset of `playedRooms`
 *
 * 3. Efficient Room Updates:
 *    - All room arrays are converted to maps for quick lookup
 *    - If a room has changed, it is removed from all maps and re-added to the appropriate one
 *    - Avoids unnecessary re-renders and preserves performance
 *
 * 4. Polling:
 *    - A background poll runs every `REFRESH_INTERVAL_FOR_ROOMS` milliseconds
 *    - It uses QUICK mode:
 *      • Only re-fetches recent and active room IDs
 *      • Much faster than FULL mode
 *      • Ensures UI stays up-to-date even if contract events are missed
 *
 * 5. Concurrency Guard (`processing` flag):
 *    - Prevents multiple parallel fetches (e.g. from manual triggers or overlapping polls)
 *
 * 6. Cleanup (`clearStore`):
 *    - Stops polling, resets all state and room lists, and clears the provider
 */
import { RoomInfo } from '../types/app-types';
import { isMocked } from '../utils/isMocked';
import { chunksSplit, isRoomEqual } from '../utils/isRoomEqual';
import {
  mockReadActiveRoomCounter,
  mockReadGetRoomsInfo,
  mockReadRoomIdCounter,
} from 'e2e-tests/mocks/mock-rooms';
import { PublicClient } from 'viem';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { fetchGamesApi } from 'shared/api/gamesApi';
import {
  FETCH_ROOMS_LIMIT,
  IS_EXPLORE_GAMES_PAGE,
  MAX_ACTIVE_ROOMS_TO_FETCH,
  MAX_ROOMS_PER_BATCH,
  NULL_ADDRESS,
  REFRESH_INTERVAL_FOR_ROOMS,
} from 'shared/constants/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { GamesFetchMode, GamesStoreState } from 'shared/types/games.types';
import {
  readActiveRoomCounter,
  readGetRoomsInfo,
  readRoomIdCounter,
} from 'shared/viem-functions/find-rooms';

export const USE_API_FETCH = import.meta.env.VITE_USE_API_FETCH === 'true';

interface Actions {
  clearStore: () => void;
  startStore: (provider: PublicClient) => Promise<void>;
  loadMorePlayedRooms: () => Promise<void>;
}

export const initialState: GamesStoreState = {
  intervalId: null,
  activeRooms: [],
  playedRooms: [],
  activeSingle: [],
  playedRoomsFirstBatch: [],
  roomIdCounter: 0,
  activeRoomCounter: 0,
  lastFetched: 0,
  loading: true,
  time: 0,
};

let activeAbortController: AbortController | null = null;

export const useGamesStore = create<GamesStoreState & Actions>()(
  immer(
    devtools((set, get) => {
      let processing = false;
      let currentProvider: PublicClient | null = null;

      const createRoomMap = (rooms: RoomInfo[]) =>
        new Map(rooms.map((r) => [Number(r.data.roomId), r]));

      const removeFromAllMaps = (
        roomId: number,
        activeRoomsMap: Map<number, RoomInfo>,
        playedRoomsMap: Map<number, RoomInfo>,
        activeSingleMap: Map<number, RoomInfo>,
        playedRoomsFirstBatchMap: Map<number, RoomInfo>,
      ) => {
        activeRoomsMap.delete(roomId);
        playedRoomsMap.delete(roomId);
        activeSingleMap.delete(roomId);
        playedRoomsFirstBatchMap.delete(roomId);
      };

      const addToRelevantMap = (
        room: RoomInfo,
        activeRoomsMap: Map<number, RoomInfo>,
        playedRoomsMap: Map<number, RoomInfo>,
        activeSingleMap: Map<number, RoomInfo>,
        playedRoomsFirstBatchMap: Map<number, RoomInfo>,
      ) => {
        const roomId = Number(room.data.roomId);

        let isClosed = room.status === RoomStatusEnum.Closed;
        const isPlayerBJoined = room.data.playerB !== NULL_ADDRESS;

        if (isPlayerBJoined) {
          if (isClosed) {
            playedRoomsMap.set(roomId, room);
            playedRoomsFirstBatchMap.set(roomId, room);
          } else {
            activeRoomsMap.set(roomId, room);
          }
        } else if (!isClosed) {
          activeSingleMap.set(roomId, room);
        }
      };

      const sortByRoomId = (a: RoomInfo, b: RoomInfo) =>
        Number(a.data.roomId) - Number(b.data.roomId);

      const fetchAndUpdateRooms = async (
        mode: GamesFetchMode.FULL | GamesFetchMode.QUICK = GamesFetchMode.QUICK,
        signal: AbortSignal,
      ) => {
        if (signal.aborted) return;
        if (!currentProvider) return;

        try {
          const newRoomIdCounter =
            isMocked && !IS_EXPLORE_GAMES_PAGE
              ? await mockReadRoomIdCounter()
              : Number(await readRoomIdCounter(currentProvider, signal));
          const newActiveRoomCounter =
            isMocked && !IS_EXPLORE_GAMES_PAGE
              ? await mockReadActiveRoomCounter()
              : Number(await readActiveRoomCounter(currentProvider, signal));

          const state = get();

          const lastUpdateTooRecent = Date.now() - state.time < REFRESH_INTERVAL_FOR_ROOMS;
          const countersChanged =
            newRoomIdCounter !== state.roomIdCounter ||
            newActiveRoomCounter !== state.activeRoomCounter;

          if (mode === GamesFetchMode.QUICK && !countersChanged && lastUpdateTooRecent) return;

          set({ loading: true });

          if (USE_API_FETCH) {
            console.log('fetchGamesApi');
            const chainId = currentProvider.chain.id;
            const apiResponse = await fetchGamesApi(chainId);

            if (!apiResponse || !Array.isArray(apiResponse)) {
              console.warn('Invalid API response:', apiResponse);
              return;
            }

            const normalizedRooms = apiResponse.map((r) => {
              const roomId = BigInt(r[0]);
              const playerA = r[1] as `0x${string}`;
              const playerB = r[2] as `0x${string}`;
              const token = r[3] as `0x${string}`;
              const bet = BigInt(Math.floor(Number(r[4]) * 1_000_000));
              const status = r[5] === 'completed' ? RoomStatusEnum.Closed : RoomStatusEnum.Open;

              return {
                closer: playerB === NULL_ADDRESS ? 1 : 0,
                data: { roomId, playerA, playerB, token, bet, wins: 0 },
                deadline: 0n,
                pot: bet,
                roundCounter: 1,
                status,
                winnerStatus: 0,
                winsCounter: { winsPlayerA: 0, winsPlayerB: 0 },
              };
            });

            const activeRooms = normalizedRooms.filter(
              (r) => r.status !== RoomStatusEnum.Closed && r.data.playerB !== NULL_ADDRESS,
            );
            const activeSingle = normalizedRooms.filter(
              (r) => r.status !== RoomStatusEnum.Closed && r.data.playerB === NULL_ADDRESS,
            );
            const playedRooms = normalizedRooms.filter(
              (r) => r.status === RoomStatusEnum.Closed && r.roundCounter > 0,
            );
            const playedRoomsFirstBatch = playedRooms.slice(0, 50);

            set({
              activeRooms: activeRooms.sort(sortByRoomId),
              activeSingle: activeSingle.sort(sortByRoomId),
              playedRooms: playedRooms.sort(sortByRoomId),
              playedRoomsFirstBatch: playedRoomsFirstBatch.sort(sortByRoomId),
              roomIdCounter: normalizedRooms.length ? Number(normalizedRooms[0].data.roomId) : 0,
              activeRoomCounter: activeRooms.length,
              lastFetched: Date.now(),
              loading: false,
              time: Date.now(),
            });

            return;
          }

          const activeRoomsMap = createRoomMap(state.activeRooms);
          const playedRoomsMap = createRoomMap(state.playedRooms);
          const activeSingleMap = createRoomMap(state.activeSingle);
          const playedRoomsFirstBatchMap = createRoomMap(state.playedRoomsFirstBatch);
          const updatedRoomIds = new Set<number>();

          if (mode === GamesFetchMode.FULL) {
            let to = newRoomIdCounter;
            let foundCount = 0;
            let fetchedRoomsCount = 0;

            while (
              to > 0 &&
              foundCount < MAX_ACTIVE_ROOMS_TO_FETCH &&
              fetchedRoomsCount < FETCH_ROOMS_LIMIT
            ) {
              const from = Math.max(to - MAX_ROOMS_PER_BATCH, 0);
              const rooms =
                isMocked && !IS_EXPLORE_GAMES_PAGE
                  ? await mockReadGetRoomsInfo(currentProvider, from, to)
                  : await readGetRoomsInfo(currentProvider, from, to);

              for (const room of rooms) {
                const roomId = Number(room.data.roomId);
                if (fetchedRoomsCount >= FETCH_ROOMS_LIMIT) break;

                fetchedRoomsCount++;

                const oldRoom = activeRoomsMap.get(roomId) || activeSingleMap.get(roomId);
                if ((oldRoom && isRoomEqual(oldRoom, room)) || room.data.playerA === NULL_ADDRESS)
                  continue;

                removeFromAllMaps(
                  roomId,
                  activeRoomsMap,
                  playedRoomsMap,
                  activeSingleMap,
                  playedRoomsFirstBatchMap,
                );
                addToRelevantMap(
                  room,
                  activeRoomsMap,
                  playedRoomsMap,
                  activeSingleMap,
                  playedRoomsFirstBatchMap,
                );

                const isNowActive =
                  room.data.playerB !== NULL_ADDRESS && room.status !== RoomStatusEnum.Closed;
                const isNowSingle =
                  room.data.playerB === NULL_ADDRESS && room.status !== RoomStatusEnum.Closed;

                if ((isNowActive || isNowSingle) && !oldRoom) {
                  foundCount++;
                }
              }

              set({
                activeRooms: [...activeRoomsMap.values()].sort(sortByRoomId),
                playedRooms: [...playedRoomsMap.values()].sort(sortByRoomId),
                activeSingle: [...activeSingleMap.values()].sort(sortByRoomId),
                playedRoomsFirstBatch: [...playedRoomsFirstBatchMap.values()]
                  .filter((room) => room.roundCounter > 0 && room.status === RoomStatusEnum.Closed)
                  .sort(sortByRoomId),
                roomIdCounter: newRoomIdCounter,
                activeRoomCounter: newActiveRoomCounter,
                lastFetched: from,
                loading: true,
                time: Date.now(),
              });

              to = from;
              if (from === 0 || fetchedRoomsCount >= FETCH_ROOMS_LIMIT) break;
            }
          } else if (mode === GamesFetchMode.QUICK) {
            const getClampedRange = (ids: number[]) => {
              const minId = Math.min(...ids);
              const maxId = Math.max(...ids);
              if (maxId - minId + 1 > MAX_ROOMS_PER_BATCH) {
                return { from: Math.max(maxId - MAX_ROOMS_PER_BATCH + 1, 1), to: maxId };
              }
              return { from: minId, to: maxId };
            };
            const activeIds = [
              ...state.activeRooms.map((r) => Number(r.data.roomId)),
              ...state.activeSingle.map((r) => Number(r.data.roomId)),
            ];

            const displayedIds = Array.from(playedRoomsFirstBatchMap.keys());

            const roomIdsToFetch = Array.from(
              new Set([...activeIds, ...displayedIds, newRoomIdCounter]),
            );

            const batches = chunksSplit(roomIdsToFetch, MAX_ROOMS_PER_BATCH);

            for (const roomId of batches) {
              const { from, to } = getClampedRange(roomId);

              const rooms =
                isMocked && !IS_EXPLORE_GAMES_PAGE
                  ? await mockReadGetRoomsInfo(currentProvider, from, to)
                  : await readGetRoomsInfo(currentProvider, from, to);

              for (const room of rooms) {
                const roomId = Number(room.data.roomId);
                updatedRoomIds.add(roomId);

                const oldRoom =
                  activeRoomsMap.get(roomId) ||
                  activeSingleMap.get(roomId) ||
                  playedRoomsMap.get(roomId);

                if ((oldRoom && isRoomEqual(oldRoom, room)) || room.data.playerA === NULL_ADDRESS) {
                  continue;
                }

                removeFromAllMaps(
                  roomId,
                  activeRoomsMap,
                  playedRoomsMap,
                  activeSingleMap,
                  playedRoomsFirstBatchMap,
                );
                addToRelevantMap(
                  room,
                  activeRoomsMap,
                  playedRoomsMap,
                  activeSingleMap,
                  playedRoomsFirstBatchMap,
                );
              }
            }

            set({
              activeRooms: [...activeRoomsMap.values()].sort(sortByRoomId),
              playedRooms: [...playedRoomsMap.values()].sort(sortByRoomId),
              activeSingle: [...activeSingleMap.values()].sort(sortByRoomId),
              playedRoomsFirstBatch: [...playedRoomsFirstBatchMap.values()]
                .filter((room) => room.roundCounter > 0 && room.status === RoomStatusEnum.Closed)
                .sort(sortByRoomId),
              roomIdCounter: newRoomIdCounter,
              activeRoomCounter: newActiveRoomCounter,
              lastFetched: state.lastFetched,
              loading: true,
              time: Date.now(),
            });
          }

          set({ loading: false });
        } catch (error) {
          console.error('Polling error:', error);
          set({ loading: false });
        }
      };

      return {
        ...initialState,
        startStore: async (provider: PublicClient) => {
          const { intervalId } = get();
          if (intervalId) clearInterval(intervalId);
          currentProvider = provider;

          if (activeAbortController) {
            activeAbortController.abort();
          }
          activeAbortController = new AbortController();
          const signal = activeAbortController.signal;

          try {
            processing = true;
            await fetchAndUpdateRooms(GamesFetchMode.FULL, signal);
          } catch (error) {
            console.error('Error during initial load:', error);
          } finally {
            processing = false;
          }

          const newIntervalId = setInterval(async () => {
            if (processing || !currentProvider) return;
            processing = true;
            try {
              await fetchAndUpdateRooms(GamesFetchMode.QUICK, signal);
            } finally {
              processing = false;
            }
          }, REFRESH_INTERVAL_FOR_ROOMS);

          set({ intervalId: newIntervalId });
        },
        loadMorePlayedRooms: async () => {
          const state = get();

          if (USE_API_FETCH) {
            const currentCount = state.playedRoomsFirstBatch.length;
            const nextBatch = state.playedRooms.slice(currentCount, currentCount + 50);

            if (nextBatch.length > 0) {
              set({
                playedRoomsFirstBatch: [...state.playedRoomsFirstBatch, ...nextBatch],
              });
            }
            return;
          }

          if (!currentProvider) return;

          const to = state.lastFetched;
          if (to <= 0) return;

          const from = Math.max(to - FETCH_ROOMS_LIMIT, 0);

          const activeRoomsMap = createRoomMap(state.activeRooms);
          const playedRoomsMap = createRoomMap(state.playedRooms);
          const activeSingleMap = createRoomMap(state.activeSingle);
          const playedRoomsFirstBatchMap = createRoomMap(state.playedRoomsFirstBatch);

          try {
            const rooms =
              isMocked && !IS_EXPLORE_GAMES_PAGE
                ? await mockReadGetRoomsInfo(currentProvider, from, to)
                : await readGetRoomsInfo(currentProvider, from, to);

            const closedRooms = rooms.filter((room) => room.status === RoomStatusEnum.Closed);

            for (const room of closedRooms) {
              playedRoomsMap.set(Number(room.data.roomId), room);
            }

            const sortedRooms = [...playedRoomsMap.values()].sort(sortByRoomId);

            for (const room of rooms) {
              const roomId = Number(room.data.roomId);

              const oldRoom =
                activeRoomsMap.get(roomId) ||
                activeSingleMap.get(roomId) ||
                playedRoomsMap.get(roomId);

              if ((oldRoom && isRoomEqual(oldRoom, room)) || room.data.playerA === NULL_ADDRESS) {
                continue;
              }

              removeFromAllMaps(
                roomId,
                activeRoomsMap,
                playedRoomsMap,
                activeSingleMap,
                playedRoomsFirstBatchMap,
              );
              addToRelevantMap(
                room,
                activeRoomsMap,
                playedRoomsMap,
                activeSingleMap,
                playedRoomsFirstBatchMap,
              );
            }

            set({
              playedRooms: sortedRooms,
              playedRoomsFirstBatch: sortedRooms.filter((room) => room.roundCounter > 0),
              lastFetched: from,
            });
          } catch (err) {
            console.error('Failed to fetch more played rooms:', err);
          }
        },

        clearStore: () => {
          const { intervalId } = get();
          if (intervalId) clearInterval(intervalId);
          set({ ...initialState });
          currentProvider = null;
          processing = false;
        },
      };
    }),
  ),
);
